import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { PixelPaths } from '@/components/pixel/pixel-sprite'

import {
    DINO_BOARD,
    DINO_BODY,
    DINO_EYE_CLOSED,
    DINO_EYE_OPEN,
    DINO_PALETTE,
    DINO_VIEWBOX,
    DINO_WHEEL,
    DINO_WHEEL_POSITIONS
} from '@/constants/sprites'

interface SkateDinoProps {
    className?: string
    interactive?: boolean
    rollIn?: boolean
    firstLine?: string
}

const TRICK_LINES = ["kickflip!", "nailed it!", "one more?", "your brand next?", "stay rad"]

const DinoSprite: FC = (): ReactNode => {
    return (
        <svg
            viewBox={DINO_VIEWBOX}
            shapeRendering='crispEdges'
            className='block h-auto w-full overflow-visible'
            aria-hidden='true'
            focusable='false'
        >
            <g className='animate-bob'>
                <PixelPaths rows={DINO_BODY} palette={DINO_PALETTE} />

                <g className='animate-blink'>
                    <PixelPaths rows={DINO_EYE_OPEN} palette={DINO_PALETTE} />
                </g>

                <g className='animate-blink-closed opacity-0'>
                    <PixelPaths rows={DINO_EYE_CLOSED} palette={DINO_PALETTE} />
                </g>
            </g>

            <PixelPaths rows={DINO_BOARD} palette={DINO_PALETTE} />

            {DINO_WHEEL_POSITIONS.map(([x, y]) => (
                <g key={x} transform={`translate(${x} ${y})`}>
                    <g className='animate-wheel [transform-box:fill-box] [transform-origin:center]'>
                        <PixelPaths rows={DINO_WHEEL} palette={DINO_PALETTE} />
                    </g>
                </g>
            ))}
        </svg>
    )
}

const SkateDino: FC<SkateDinoProps> = ({
    className,
    interactive = false,
    rollIn = false,
    firstLine = "rawr! tap me"
}): ReactNode => {
    const [tricks, setTricks] = useState(0)

    const line = tricks === 0 ? firstLine : TRICK_LINES[(tricks - 1) % TRICK_LINES.length]

    return (
        <div className={cn('relative', rollIn && 'animate-roll-in [--roll-delay:600ms]', className)}>
            {interactive ? (
                <>
                    <button
                        type='button'
                        onClick={() => setTricks(count => count + 1)}
                        aria-label="Make the dino do a kickflip"
                        className='block w-full cursor-pointer'
                    >
                        <span key={tricks} className={cn('block origin-bottom', tricks > 0 && 'animate-ollie')}>
                            <DinoSprite />
                        </span>
                    </button>

                    <span
                        aria-hidden='true'
                        className='pointer-events-none absolute right-[8%] bottom-[calc(100%+14px)] animate-float'
                    >
                        <span
                            key={line}
                            className='relative block animate-pop-in whitespace-nowrap border-2 border-ink bg-white px-3 py-1.5 font-heading text-sm text-ink shadow-pixel-sm md:text-base'
                        >
                            {line}
                            <span className='absolute -bottom-[10px] right-7 size-2 bg-ink' />
                            <span className='absolute -bottom-[18px] right-5 size-2 bg-ink' />
                        </span>
                    </span>

                    <span className='sr-only' aria-live='polite'>
                        {tricks > 0 ? `Tricks landed: ${tricks}` : ''}
                    </span>
                </>
            ) : (
                <DinoSprite />
            )}
        </div>
    )
}

export default SkateDino
